import { useCallback, useMemo, useState } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import type {
  ProductId,
  ProductVariantId,
  StockBalanceId,
  StockLocationId,
  StoreId,
} from "@KassaroAB/shared/types/ids";
import type { RestProduct } from "@KassaroAB/shared/types/product";
import type {
  RestQueryStockBalances,
  RestSetStockBalances,
  RestStockBalance,
} from "@KassaroAB/shared/types/stockBalance";
import type { StockPolicyAssignment } from "@KassaroAB/shared/types/stockPolicy";
import { stockBalanceDocId } from "@KassaroAB/shared/utils/stockBalance";
import {
  queryStockBalancesApi,
  setStockBalancesApi,
} from "../../../api/stockBalancesApi";

const PRODUCT_WINDOW_SIZE = 100;
const QUERY_CHUNK_SIZE = 1000;
const SET_CHUNK_SIZE = 5000;

type StockEdit = RestSetStockBalances[number];
type StockSku = RestQueryStockBalances["items"][number];

export interface ProductTableStock {
  stockLocationId: StockLocationId | null;
  isLoading: (productId: ProductId) => boolean;
  getQuantity: (
    productId: ProductId,
    variantId: ProductVariantId | null,
  ) => number | null;
  isDirty: (
    productId: ProductId,
    variantId: ProductVariantId | null,
  ) => boolean;
  getStockPolicy: (
    productId: ProductId,
    variantId: ProductVariantId | null,
  ) => StockPolicyAssignment | null;
  setQuantity: (
    productId: ProductId,
    variantId: ProductVariantId | null,
    quantity: number | null,
  ) => void;
}

interface UseProductTableStockParams {
  storeId: StoreId | undefined;
  stockLocationId: StockLocationId | null;
  rows: RestProduct[];
}

const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, (index + 1) * size),
  );

const combineBalances = (
  results: { data?: RestStockBalance[]; isPending: boolean }[],
) => ({
  balances: new Map(
    results.flatMap((result) =>
      (result.data ?? []).map((balance) => [balance.id, balance]),
    ),
  ),
  pending: results.map((result) => result.isPending),
});

export const useProductTableStock = ({
  storeId,
  stockLocationId,
  rows,
}: UseProductTableStockParams) => {
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState<Record<StockBalanceId, StockEdit>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const skuChunks = useMemo((): StockSku[][] => {
    if (!storeId || !stockLocationId) {
      return [];
    }
    return chunk(rows, PRODUCT_WINDOW_SIZE).flatMap((window) =>
      chunk(
        window.flatMap((product): StockSku[] =>
          product.variants?.length
            ? product.variants.map((variant) => ({
                productId: product.id,
                variantId: variant.id,
              }))
            : [{ productId: product.id, variantId: null }],
        ),
        QUERY_CHUNK_SIZE,
      ),
    );
  }, [rows, storeId, stockLocationId]);

  const balances = useQueries({
    queries: skuChunks.map((items) => ({
      queryKey: ["stores", storeId, "stockBalances", stockLocationId, items],
      queryFn: () =>
        queryStockBalancesApi(storeId!, {
          stockLocationId: stockLocationId!,
          items,
        }),
    })),
    combine: combineBalances,
  });
  const pendingProductIds = useMemo(
    () =>
      new Set(
        skuChunks.flatMap((items, index) =>
          balances.pending[index] ? items.map((item) => item.productId) : [],
        ),
      ),
    [skuChunks, balances.pending],
  );

  const stock = useMemo((): ProductTableStock => {
    const keyOf = (productId: ProductId, variantId: ProductVariantId | null) =>
      stockBalanceDocId(stockLocationId!, productId, variantId);
    return {
      stockLocationId,
      isLoading: (productId) => pendingProductIds.has(productId),
      getQuantity: (productId, variantId) => {
        const key = keyOf(productId, variantId);
        return key in edits
          ? edits[key].quantity
          : (balances.balances.get(key)?.quantity ?? null);
      },
      isDirty: (productId, variantId) => keyOf(productId, variantId) in edits,
      getStockPolicy: (productId, variantId) =>
        balances.balances.get(keyOf(productId, variantId))?.stockPolicy ?? null,
      setQuantity: (productId, variantId, quantity) => {
        const key = keyOf(productId, variantId);
        setEdits((previous) => {
          const next = { ...previous };
          if (quantity === (balances.balances.get(key)?.quantity ?? null)) {
            delete next[key];
          } else {
            next[key] = {
              stockLocationId: stockLocationId!,
              productId,
              variantId,
              quantity,
            };
          }
          return next;
        });
      },
    };
  }, [stockLocationId, balances, pendingProductIds, edits]);

  const editCount = Object.keys(edits).length;

  const save = useCallback(async () => {
    if (!storeId || Object.keys(edits).length === 0) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    const saved: [string, StockEdit][] = [];
    const saveChunks = async () => {
      for (const entries of chunk(Object.entries(edits), SET_CHUNK_SIZE)) {
        await setStockBalancesApi(
          storeId,
          entries.map(([, edit]) => edit),
        );
        saved.push(...entries);
      }
    };
    await saveChunks().catch((error) => {
      setSaveError(error instanceof Error ? error.message : "Unknown error");
    });
    await Promise.all(
      ["stockBalances", "stockWarnings"].map((collection) =>
        queryClient.invalidateQueries({
          queryKey: ["stores", storeId, collection],
        }),
      ),
    );
    setEdits((previous) => {
      const next = { ...previous };
      saved.forEach(([key, edit]) => {
        if (next[key as StockBalanceId] === edit) {
          delete next[key as StockBalanceId];
        }
      });
      return next;
    });
    setIsSaving(false);
  }, [storeId, edits, queryClient]);

  return {
    stock,
    editCount,
    save,
    isSaving,
    saveError,
  };
};
